import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { supabase } from '../../../services/api/client';
import { getDriverTrackingPosition } from '../../../services/api/realtime';
import { SERVICE_STATUSES, ROLES } from '../../../constants';

// ── Iconos SVG Personalizados ──────────────────────────────────────────────
const createSVGIcon = (color, type = 'marker') => {
  const svg = type === ROLES.DRIVER 
    ? `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="${color}" fill-opacity="0.2"/>
        <circle cx="12" cy="12" r="6" fill="${color}"/>
        <path d="M12 2L14.5 9H9.5L12 2Z" fill="white" transform="rotate(0 12 12)"/>
       </svg>`
    : `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z" fill="${color}"/>
        <circle cx="12" cy="9" r="2.5" fill="white"/>
       </svg>`;

  return L.divIcon({
    html: svg,
    className: 'custom-svg-icon',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

const icons = {
  pickup: createSVGIcon('var(--bry-accent)'),
  dropoff: createSVGIcon('var(--bry-bg-secondary)'),
  driver: createSVGIcon('var(--bry-accent)', ROLES.DRIVER),
  refPoint: createSVGIcon('var(--bry-accent)'),
};

// Componente para ajustar el mapa a los puntos
function MapAutoFit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points && points.length > 0) {
      const validPoints = points.filter(p => p && p.lat && p.lng);
      if (validPoints.length > 0) {
        const bounds = L.latLngBounds(validPoints.map(p => [p.lat, p.lng]));
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [points, map]);
  return null;
}

const TrackingMap = ({ service, height = "250px" }) => {
  const [driverPos, setDriverPos] = useState(null);
  const [routePath, setRoutePath] = useState([]);
  const pickup = service?.pickup;
  const dropoff = service?.dropoff;
  const status = service?.status;
  const refPoints = service?.reference_points || [];
  let parsedRefPoints = refPoints;
  if (typeof refPoints === 'string') {
    try {
      parsedRefPoints = JSON.parse(refPoints);
    } catch {
      parsedRefPoints = [];
    }
  }

  // ── Tracking del Conductor ──────────────────────────────────────────────
  useEffect(() => {
    if (!service?.driver_uid) return;

    const fetchDriverPos = async () => {
      const { data } = await getDriverTrackingPosition(service.driver_uid);
      
      if (data) setDriverPos({ lat: data.lat, lng: data.lng });
    };

    fetchDriverPos();

    const channel = supabase
      .channel(`tracking-${service.service_id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'drivers_tracking',
        filter: `driver_uid=eq.${service.driver_uid}`
      }, payload => {
        setDriverPos({ lat: payload.new.lat, lng: payload.new.lng });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [service]);

  // ── Cálculo de Ruta Real (OSRM) ──────────────────────────────────────────
  useEffect(() => {
    const fetchRoute = async () => {
      if (!pickup || !dropoff) return;

      // Definir puntos según el estado
      let start = [pickup.lng, pickup.lat];
      let end = [dropoff.lng, dropoff.lat];

      // Si el conductor ya está asignado y tenemos su posición, trazar desde él
      if (driverPos && (status === SERVICE_STATUSES.DRIVER_ASSIGNED || status === SERVICE_STATUSES.IN_PROGRESS)) {
        start = [driverPos.lng, driverPos.lat];
        end = status === SERVICE_STATUSES.DRIVER_ASSIGNED ? [pickup.lng, pickup.lat] : [dropoff.lng, dropoff.lat];
      }

      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${start[0]},${start[1]};${end[0]},${end[1]}?overview=full&geometries=geojson`;
        const res = await fetch(url);
        const data = await res.json();
        
        if (data.routes && data.routes[0]) {
          const coords = data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
          setRoutePath(coords);
        } else {
          // Fallback a línea recta si falla la API
          setRoutePath([[start[1], start[0]], [end[1], end[0]]]);
        }
      } catch (err) {
        console.error('Error fetching route:', err);
        setRoutePath([[start[1], start[0]], [end[1], end[0]]]);
      }
    };

    fetchRoute();
  }, [pickup, dropoff, driverPos, status]);

  if (!pickup || !dropoff) return <div className="text-center p-4 bg-light rounded-16">Mapa no disponible</div>;

  const points = [pickup, dropoff, driverPos, ...parsedRefPoints].filter(Boolean);

  return (
    <div className="w-full" style={{ height }}>
      <MapContainer 
        center={[pickup.lat, pickup.lng]} 
        zoom={14} 
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        
        {/* Marcadores Estáticos */}
        <Marker position={[pickup.lat, pickup.lng]} icon={icons.pickup}>
          <Popup>Punto de Recogida</Popup>
        </Marker>
        <Marker position={[dropoff.lat, dropoff.lng]} icon={icons.dropoff}>
          <Popup>Destino</Popup>
        </Marker>

        {/* Marcador del Conductor (Dinámico) */}
        {driverPos && (
          <Marker position={[driverPos.lat, driverPos.lng]} icon={icons.driver}>
            <Popup>Tu Conductor</Popup>
          </Marker>
        )}

        {/* Puntos de Referencia */}
        {parsedRefPoints.map((rp, i) => (
          <Marker key={i} position={[rp.lat, rp.lng]} icon={icons.refPoint}>
            <Popup>{rp.label || `Punto ${i + 1}`}</Popup>
          </Marker>
        ))}

        {/* Ruta Real trazada */}
        {routePath.length > 0 && (
          <Polyline 
            positions={routePath} 
            color="var(--bry-accent)" 
            weight={5} 
            opacity={0.8}
            lineJoin="round"
          />
        )}

        {/* Línea guía (opcional, más suave) */}
        <Polyline 
          positions={[[pickup.lat, pickup.lng], [dropoff.lat, dropoff.lng]]} 
          color="var(--bry-bg-secondary)" 
          weight={2} 
          dashArray="5, 10" 
          opacity={0.3} 
        />

        <MapAutoFit points={points} />
      </MapContainer>
    </div>
  );
};

export default TrackingMap;
